// Central currency helpers — used across every financial rendering surface.
// One source of truth so USD/EUR/GBP/ETB/COP display, symbol, and formatting
// stay consistent between Budget tab, Attention Panel, Quick Add previews, dialogs, etc.

export const SUPPORTED_CURRENCIES = [
  "USD", "EUR", "GBP", "ETB", "COP", "CAD", "AUD", "NZD",
  "JPY", "CNY", "INR", "BRL", "MXN", "ZAR", "NGN", "KES",
  "AED", "SAR", "CHF", "SEK", "NOK", "DKK", "SGD", "HKD",
];

// Explicit, non-ambiguous symbols. We do NOT rely on Intl.NumberFormat's
// currencyDisplay:"narrowSymbol" because it collapses several Latin-American
// currencies (e.g. COP, MXN, BRL) to a bare "$" in en-US, which is exactly
// what caused Colombia budgets to look identical to US Dollar budgets.
export const CURRENCY_SYMBOLS = {
  USD: "$", EUR: "€", GBP: "£", CAD: "CA$", AUD: "A$", NZD: "NZ$",
  JPY: "¥", CNY: "¥", INR: "₹", BRL: "R$", MXN: "MX$", ZAR: "R",
  NGN: "₦", KES: "KSh", ETB: "Br", COP: "COL$", AED: "د.إ", SAR: "﷼",
  CHF: "CHF", SEK: "kr", NOK: "kr", DKK: "kr", SGD: "S$", HKD: "HK$",
};

export function currencySymbol(code) {
  return CURRENCY_SYMBOLS[String(code || "USD").toUpperCase()] || "$";
}

export function normalizeCurrency(code, fallback = "USD") {
  const v = String(code || "").trim().toUpperCase();
  return SUPPORTED_CURRENCIES.includes(v) ? v : fallback;
}

// Symbols ending in a letter (Br, kr, KSh, CHF) get a space before the number
// for legibility. Sigil-only symbols ($, €, £, ¥, ₹, ₦, R, R$, COL$, CA$…) sit
// flush against the digits, matching how these are conventionally written.
function needsSpace(sym) {
  // Test the last character — letters/Arabic get a space.
  const last = sym.slice(-1);
  return /[A-Za-z\u0600-\u06FF]/.test(last);
}

/**
 * Format a number in the given currency using our curated symbol map.
 *  fmt(1250.5, 'EUR') → "€1,250.50"
 *  fmt(4000000, 'COP') → "COL$4,000,000"
 *  fmt(-800000, 'COP') → "-COL$800,000"
 *  fmt(150, 'ETB') → "Br 150"
 */
export function fmtMoney(n, currency = "USD", opts = {}) {
  const num = Number(n) || 0;
  const ccy = normalizeCurrency(currency);
  const { maximumFractionDigits = 2, minimumFractionDigits = 0 } = opts;
  const abs = Math.abs(num).toLocaleString("en-US", { maximumFractionDigits, minimumFractionDigits });
  const sym = currencySymbol(ccy);
  const sep = needsSpace(sym) ? " " : "";
  return num < 0 ? `-${sym}${sep}${abs}` : `${sym}${sep}${abs}`;
}
